import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { fetcher } from "../../../../services/fetcher";
import { Text, Switch } from "@mantine/core";
import MainLoader from "../../../../common/components/Loader/Loader";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";
import ConfirmModal from "../../../../common/components/ConfirmModal/ConfirmModal";
import useRbac from "../../../../common/hooks/useRbac/useRbac"; 
import { USERCREATE_API } from "../../../../api/endpoints";
import classes from "./User.module.css";
import DataTable from "../../../../common/components/DataTable/DataTable";

const delUser = "auth-api/users-deactivate/";

const isUserActive = (value) =>
  value === true ||
  value === 1 ||
  String(value ?? "")
    .trim()
    .toLowerCase() === "true";

const UsersPage = () => {
  const { checkPermission } = useRbac();
  const { isLoading, data: users, error, mutate } = useSWR(USERCREATE_API, fetcher);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleSwitchChange = (event, data) => {
    const isChecked = event.currentTarget.checked;
    const active = isUserActive(data?.is_active);

    if ((active && !isChecked) || (!active && isChecked)) {
      setSelectedUser(data);
      open();
    }
  };

  const deletUserMutation = async (user) => {
    try {
      await fetcher(`${delUser}`, "POST", user);
      await mutate();
      successNotifications(
        user.is_active
          ? "Employee profile enabled successfully"
          : "Employee profile disabled successfully"
      );
    } catch (error) {
      console.error(error);
      errorNotifications(error.response?.data || "An error occurred", error);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      const payload = {
        email: selectedUser.email,
        is_active: !isUserActive(selectedUser.is_active),
      };

      deletUserMutation(payload);
      setSelectedUser(null);
    }
  };

  const filteredUsers = users?.filter((user) => {
    const { user_profile_info, code } = user;
    const { user: userInfo } = user_profile_info;
    const { first_name, last_name, email } = userInfo;
    const { city, state } = user_profile_info;

    const query = searchQuery.toLowerCase();

    return (
      first_name.toLowerCase().includes(query) ||
      last_name.toLowerCase().includes(query) ||
      email.toLowerCase().includes(query) ||
      (city && city.toLowerCase().includes(query)) ||
      (state && state.toLowerCase().includes(query)) ||
      (code && code.toLowerCase().includes(query))
    );
  });

  const sortedUsers = filteredUsers
    ? [...filteredUsers].sort((a, b) => {
        const aActive = isUserActive(a?.user_profile_info?.user?.is_active);
        const bActive = isUserActive(b?.user_profile_info?.user?.is_active);
        if (aActive === bActive) return 0;
        return aActive ? -1 : 1; // active first, inactive at bottom
      })
    : [];

  if (isLoading) {
    return <MainLoader />;
  }

  if (error) {
    return (
      <>
        <p>Error {error}</p>
      </>
    );
  }

  const config = {
    fields: [
      { key: "code", label: "Emp Code" },
      { key: "user_profile_info.user.first_name", label: "First Name" },
      { key: "user_profile_info.user.last_name", label: "Last Name" },
      { key: "user_profile_info.user.email", label: "Email" },
      { key: "department_name", label: "Department" },
      {
        key: "is_active",
        label: "Status",
        render: (row) => {
          const userInfo = row?.user_profile_info?.user;
          return (
            <Switch
              checked={isUserActive(userInfo?.is_active)}
              onChange={(event) => handleSwitchChange(event, userInfo)}
              color={isUserActive(userInfo?.is_active) ? "green" : "gray"}
            />
          );
        },
      },
    ],
    // actions: [
    //   { label: "View", to: (row) => `${row.id}/` },
    // ],
    selectionActions: checkPermission("create")
      ? [
          {
            label: "+ Add New Employee",
            handler: () => navigate("create"),
          },
        ]
      : [],
    rowClickTo: (row) => `${row.id}/user-profile`,
  };

  return (
    <>
      <div className={classes.userTable}>
        {users ? (
          <DataTable
            data={sortedUsers}
            config={config}
            loading={isLoading}
            onSearch={(event) => handleSearch(event.target.value)}
          />
        ) : (
          <Text>No users available</Text>
        )}
      </div>

      <ConfirmModal
        opened={opened}
        onClose={close}
        onConfirm={handleDeleteConfirm}
        title={
          <Text className={classes.confirmModalTitle}>
            {isUserActive(selectedUser?.is_active)
              ? "Are you sure to Disable this Employee Profile?"
              : "Are you sure to Enable this Employee Profile?"}
          </Text>
        }
        confirmLabel="Yes"
        cancelLabel="No"
      >
        {isUserActive(selectedUser?.is_active)
          ? "Disabling this profile will no longer allow this employee to make any operations."
          : "Enabling this profile will allow this employee to resume operations."}
      </ConfirmModal>
    </>
  );
};

export default UsersPage;